import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCabinetId } from '@/hooks/useCabinetMemberMe';
import type { CanvasDraft } from '@hector/shared-types';
import { canvasDraftApi } from '../api/canvasDraft.api';

/**
 * Les brouillons des AUTRES conversations, dans l'ordre reçu.
 *
 * ⚠️ Les deux listes du menu « Ouvrir » ne se recouvrent pas (2026-09-23) : un
 * document de CE fil se bascule depuis « Documents de cette conversation ».
 * Le laisser aussi dans la liste des brouillons à copier, c'était offrir de le
 * copier dans son propre fil — un doublon par clic, jusqu'au plafond, y compris
 * du document ouvert.
 */
export function brouillonsDesAutresConversations(
  drafts: readonly CanvasDraft[],
  conversationId: string,
): CanvasDraft[] {
  return drafts.filter((draft) => draft.conversationId !== conversationId);
}

/**
 * Brouillons canvas récents du user (scopé cabinet) hors de la conversation
 * courante — back de « Copier un brouillon d’une autre conversation ».
 * `enabled` piloté par l'ouverture du dropdown : on ne fetch qu'à la demande,
 * pas au montage du header canvas.
 *
 * `staleTime: 0` : un brouillon auto-sauvé juste avant doit apparaître dès la
 * réouverture du menu, sans câbler d'invalidation depuis `useCanvasAutoSave`
 * (choix v1 volontairement simple — chaque réouverture re-fetch la liste).
 *
 * La requête garde sa limite de 20 AVANT le filtre : la liste peut donc en
 * montrer moins, quand certains des plus récents sont de ce fil.
 */
export function useRecentDrafts(options: { enabled: boolean; conversationCourante: string }): {
  drafts: CanvasDraft[];
  isLoading: boolean;
} {
  const cabinetId = useCabinetId();
  const query = useQuery({
    queryKey: ['canvas-drafts', 'recent', cabinetId],
    queryFn: () => canvasDraftApi.listRecent(20),
    enabled: options.enabled && !!cabinetId,
    staleTime: 0,
  });
  const drafts = React.useMemo(
    () => brouillonsDesAutresConversations(query.data ?? [], options.conversationCourante),
    [query.data, options.conversationCourante],
  );
  return { drafts, isLoading: query.isLoading };
}
